import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";
import { TOUS_SERVICES, POWER_GROUPS } from "../utils/taxonomy";
import { initials, PowerRequest, CreateRequestModal, RequestDetailModal } from "../components/PowerRequestModals";

// Marché de prestataires rattaché à un lieu précis (accessible via le bouton "Prestataires"
// sur Venue.tsx) — mêmes demandes/devis que /power-requests, mais scopées à ce lieu : ce qui
// permet à un lieu de faire aussi venir des pros sur la plateforme, de façon discrète.
export default function VenueProviders() {
  const { id } = useParams();
  const { user } = useApp();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<any>(null);
  const [requests, setRequests] = useState<PowerRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState<PowerRequest | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [editCategories, setEditCategories] = useState<string[]>([]);
  const [editExcluded, setEditExcluded] = useState<string[]>([]);
  const [savingCategories, setSavingCategories] = useState(false);

  const isOwner = !!venue && venue.owner_id === user?.id;

  useEffect(() => {
    if (!id) return;
    api.get(`/venues/${id}`).then(data => data?.venue && setVenue(data.venue)).catch(() => {});
  }, [id]);

  const saveCategories = () => {
    if (!id) return;
    setSavingCategories(true);
    // Le PUT ne renvoie que les colonnes brutes, pas les champs calculés (has_provider_section,
    // provider_categories_effective/auto) dont dépendent les filtres et le bouton "Prestataires"
    // — on recharge le lieu en entier pour que l'état local reste cohérent après l'enregistrement.
    api.put(`/venues/${id}`, { provider_categories: editCategories, excluded_provider_categories: editExcluded })
      .then(data => {
        if (data?.venue) {
          api.get(`/venues/${id}`).then(fresh => fresh?.venue && setVenue(fresh.venue)).catch(() => {});
          setShowCategoriesModal(false);
        } else alert(data?.error || "Erreur lors de la mise à jour");
      })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setSavingCategories(false));
  };

  const load = () => {
    if (!id) return;
    setLoading(true);
    const qs = new URLSearchParams({ venue_id: id });
    if (categoryFilter) qs.set("category", categoryFilter);
    api.get(`/publications/requests/nearby?${qs.toString()}`)
      .then(data => setRequests(data?.requests || []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id, categoryFilter]);

  const venueCategories: string[] = venue?.provider_categories_effective?.length > 0 ? venue.provider_categories_effective : TOUS_SERVICES;

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>Prestataires</div>
              {venue?.name && <div style={{ fontSize: 12.5, color: "#999" }}>{venue.name}</div>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {isOwner && (
              <button onClick={() => { setEditCategories(venue?.provider_categories || []); setEditExcluded(venue?.excluded_provider_categories || []); setShowCategoriesModal(true); }} style={{ backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 20, padding: "9px 16px", fontSize: 13, fontWeight: 600, color: "#0a0a0a", cursor: "pointer" }}>
                Catégories proposées
              </button>
            )}
            <button onClick={() => setShowCreate(true)} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Publier une demande</button>
          </div>
        </div>
        <div style={{ padding: "10px 24px 0", fontSize: 13, color: "#999" }}>
          {isOwner
            ? "Choisis les services que ce lieu propose (optionnel, comme l'agenda) pour attirer des prestataires, et réponds ici aux demandes reçues avec un devis."
            : "Demandes ouvertes chez ce lieu, les prestataires répondent avec un devis."}
        </div>

        <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 6, padding: "16px 24px 0", overflowX: "auto" }}>
          <style>{`.oltodo-no-scrollbar::-webkit-scrollbar{display:none} .oltodo-no-scrollbar{scrollbar-width:none;-ms-overflow-style:none}`}</style>
          <button onClick={() => setCategoryFilter("")} style={{ flexShrink: 0, padding: "6px 14px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", border: categoryFilter === "" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: categoryFilter === "" ? "#0a0a0a" : "white", color: categoryFilter === "" ? "white" : "#666" }}>
            Toutes
          </button>
          {venueCategories.map((c: string) => (
            <button key={c} onClick={() => setCategoryFilter(prev => prev === c ? "" : c)} style={{ flexShrink: 0, padding: "6px 14px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", border: categoryFilter === c ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: categoryFilter === c ? "#0a0a0a" : "white", color: categoryFilter === c ? "white" : "#666" }}>
              {c}
            </button>
          ))}
        </div>

        {loading && <div style={{ textAlign: "center", padding: 60, color: "#999" }}>Chargement…</div>}
        {!loading && requests.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}>
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><line x1="2" y1="13" x2="22" y2="13"/>
            </svg>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Aucune demande ouverte pour l'instant</div>
          </div>
        )}

        {!loading && requests.length > 0 && (
          <div style={{ padding: "16px 24px" }}>
            {requests.map(r => (
              <div key={r.id} onClick={() => setDetail(r)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 0", borderBottom: "1px solid #F5F5F5", cursor: "pointer" }}>
                <div style={{ width: 42, height: 42, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, overflow: "hidden", flexShrink: 0 }}>
                  {r.author_avatar ? <img src={r.author_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(r.author_name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#0a0a0a", backgroundColor: "#F0F0F0", borderRadius: 10, padding: "2px 8px" }}>{r.category}</span>
                    {Number(r.offers_count) > 0 && <span style={{ fontSize: 11, color: "#999" }}>{r.offers_count} devis reçu{Number(r.offers_count) > 1 ? "s" : ""}</span>}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{r.title}</div>
                  <div style={{ fontSize: 12.5, color: "#666", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const }}>{r.description}</div>
                  <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>{r.is_free ? "Budget non précisé" : `Budget indicatif : ${r.price}€`} · {r.author_name}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateRequestModal
          categories={venueCategories}
          venueId={id}
          venueName={venue?.name}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {detail && <RequestDetailModal request={detail} isOwner={detail.user_id === user?.id} onClose={() => setDetail(null)} onChanged={load} />}

      {/* Catégories de services proposées à ce lieu — cochées ici, ou détectées automatiquement
          si le gestionnaire a déjà déclaré des Powers ; dès qu'il y en a une, le bouton
          "Prestataires" apparaît pour les visiteurs sur la fiche du lieu. */}
      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showCategoriesModal && ReactDOM.createPortal(
        <div onClick={() => setShowCategoriesModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 460, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 700 }}>Prestations proposées</span>
              <button onClick={() => setShowCategoriesModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <div style={{ fontSize: 12.5, color: "#999", marginBottom: 16 }}>
              Coche les services proposés sur place, optionnel, comme l'agenda. Le bouton "Prestataires" apparaîtra pour les visiteurs dès qu'au moins une catégorie est sélectionnée.
            </div>
            {venue?.provider_categories_auto?.length > 0 && (
              <div style={{ marginBottom: 18, paddingBottom: 16, borderBottom: "1px solid #F0F0F0" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#666", marginBottom: 4 }}>Depuis tes Powers</div>
                <div style={{ fontSize: 11.5, color: "#999", marginBottom: 8 }}>
                  Détectées automatiquement sur ton profil, décoche celles qui n'ont rien à voir avec ce lieu.
                </div>
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                  {venue.provider_categories_auto.map((item: string) => {
                    const included = !editExcluded.includes(item);
                    return (
                      <button
                        key={item}
                        onClick={() => setEditExcluded(prev => included ? [...prev, item] : prev.filter(c => c !== item))}
                        style={{ padding: "7px 12px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer", border: included ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: included ? "#0a0a0a" : "white", color: included ? "white" : "#bbb" }}
                      >
                        {included ? "✓ " : ""}{item}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ fontSize: 12, fontWeight: 700, color: "#666", marginBottom: 8 }}>Autres services</div>
            {POWER_GROUPS.filter(g => g.family !== "Autre").map(g => (
              <div key={g.family} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#666", marginBottom: 8 }}>{g.family}</div>
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                  {g.items.map(item => {
                    const checked = editCategories.includes(item);
                    return (
                      <button
                        key={item}
                        onClick={() => setEditCategories(prev => checked ? prev.filter(c => c !== item) : [...prev, item])}
                        style={{ padding: "7px 12px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer", border: checked ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: checked ? "#0a0a0a" : "white", color: checked ? "white" : "#666" }}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button onClick={saveCategories} disabled={savingCategories} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: savingCategories ? 0.5 : 1, marginTop: 8 }}>
              {savingCategories ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
